import { section } from '@/neon';
import Flickity from 'flickity';
import { add, listen, qs, qsa } from '@fluorescent/dom';

import isFirstSectionOnHomepage from '@/lib/homePage';
import bp from '@/lib/breakpoints';
import { prefersReducedMotion } from '@/lib/a11y';
import pageDots from '@/lib/pageDots';

const selectors = {
  slideshow: '.js-slideshow',
  slide: '.slideshow__slide',
  images: '.slideshow__image',
  desktopImages: '.slideshow__image--desktop',
  mobileImages: '.slideshow__image--mobile',
  innerImage: '.image__img',
};

section('slideshow', {
  slideshow: null,
  events: [],

  onLoad() {
    this.slideshowContainer = qs(selectors.slideshow, this.container);
    this.slides = qsa(selectors.slide, this.container);
    this.slideImagesDesktop = qsa(selectors.desktopImages, this.container);
    this.slideImagesMobile = qsa(selectors.mobileImages, this.container);

    const docStyle = document.documentElement.style;
    this.transformProp =
      typeof docStyle.transform == 'string' ? 'transform' : 'WebkitTransform';

    if (isFirstSectionOnHomepage(this.container)) {
      add(this.container, 'first-full-height');
    }

    this._initSlideshow();
  },

  _initSlideshow() {
    const slideshowOpts = {
      percentPosition: true,
      wrapAround: true,
      prevNextButtons: false,
      adaptiveHeight: true,
      pageDots: false,
    };

    const { timer, slideCount } = this.container.dataset;
    slideshowOpts.autoPlay = parseInt(timer);

    if (this.slides.length > 1) {
      this.slideshow = new Flickity(this.slideshowContainer, {
        ...slideshowOpts,
      });

      this.pageDots = pageDots(this.container, this.slideshow, slideCount);

      this.slideshow.on('scroll', () => {
        if (!prefersReducedMotion()) {
          this.slideshow.slides.forEach((slide, i) => {
            this._handleSlideScroll(slide, i);
          });
        }
      });

      this.slideshow.on('select', () => {
        this.pageDots.update(this.slideshow.selectedIndex);
      });

      this.navListener = listen(this.container, 'click', evt => {
        this._clickListener(evt, this.slideshow);
      });
    } else if (this.slides.length === 1) {
      this.slides[0].classList.add('is-selected');
    }
  },

  _clickListener(evt, slideshow) {
    const { control } = evt.target.dataset;

    if (control === 'next') {
      evt && evt.preventDefault();
      slideshow.next();
    } else if (control === 'prev') {
      evt && evt.preventDefault();
      slideshow.previous();
    } else if (control === 'pause') {
      evt && evt.preventDefault();
      if (slideshow.player.state === 'playing') {
        slideshow.stopPlayer();
      } else {
        slideshow.playPlayer();
      }
    }
  },

  _handleSlideScroll(slide, index) {
    let img;

    if (bp.isSmall()) {
      img = this.slideImagesMobile[index];
    } else {
      img = this.slideImagesDesktop[index];
    }

    let x = 0;

    if (index === 0) {
      x =
        Math.abs(this.slideshow.x) > this.slideshow.slidesWidth
          ? this.slideshow.slidesWidth +
            this.slideshow.x +
            this.slideshow.slides[this.slideshow.slides.length - 1].outerWidth +
            slide.target
          : slide.target + this.slideshow.x;
    } else if (index === this.slideshow.slides.length - 1) {
      x =
        Math.abs(this.slideshow.x) + this.slideshow.slides[index].outerWidth <
        this.slideshow.slidesWidth
          ? slide.target -
            this.slideshow.slidesWidth +
            this.slideshow.x -
            this.slideshow.slides[index].outerWidth
          : slide.target + this.slideshow.x;
    } else {
      x = slide.target + this.slideshow.x;
    }

    img.style[this.transformProp] = 'translateX(' + (x * -1) / 2 + 'px)';
  },

  onUnload() {
    if (this.slideshow) {
      this.slideshow.destroy();
      this.pageDots.unload();
    }

    this.atBreakpointChange.unload();
    this.events.forEach(unsubscribe => unsubscribe());
  },

  onBlockSelect({ target: slide }) {
    if (this.slideshow) {
      this.slideshow.pausePlayer();
      this.slideshow.select(slide.dataset.index, true, true);
    }
  },

  onBlockDeselect() {
    if (this.slideshow) {
      this.slideshow.unpausePlayer();
    }
  },
});
